import { useState, useRef, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Camera, Upload, X, CheckCircle2, AlertTriangle, Loader2, Sparkles,
  Wrench, Clock, Gauge, ClipboardCheck, ImagePlus, Star, FileSignature,
  Ship, Cog, ListChecks
} from "lucide-react";

interface PMSJobCompletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: any | null;
  userId?: string;
  fullName?: string | null;
  onCompleted?: () => void;
}

const SEA_STATES = [
  { value: "calm", label: "Calm (0-1)" },
  { value: "smooth", label: "Smooth (2)" },
  { value: "slight", label: "Slight (3)" },
  { value: "moderate", label: "Moderate (4)" },
  { value: "rough", label: "Rough (5)" },
  { value: "very_rough", label: "Very Rough (6+)" },
];

const CONDITION_LABELS = ["", "Critical", "Poor", "Fair", "Good", "Excellent"];
const CONDITION_COLORS = ["", "text-red-600", "text-orange-500", "text-amber-500", "text-emerald-500", "text-emerald-600"];

const PMSJobCompletionDialog = ({
  open, onOpenChange, job, userId, fullName, onCompleted,
}: PMSJobCompletionDialogProps) => {
  const { toast } = useToast();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(1);
  const [workDescription, setWorkDescription] = useState("");
  const [workPerformed, setWorkPerformed] = useState("");
  const [partsUsed, setPartsUsed] = useState("");
  const [hoursSpent, setHoursSpent] = useState<string>("");
  const [vesselRunningHours, setVesselRunningHours] = useState<string>("");
  const [weatherConditions, setWeatherConditions] = useState("");
  const [seaState, setSeaState] = useState<string>("");
  const [conditionRating, setConditionRating] = useState<number>(0);
  const [defectFound, setDefectFound] = useState(false);
  const [defectDescription, setDefectDescription] = useState("");
  const [followUpRequired, setFollowUpRequired] = useState(false);
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [signatureName, setSignatureName] = useState(fullName || "");
  const [photos, setPhotos] = useState<{ file: File; preview: string; uploading: boolean; url?: string }[]>([]);

  const reset = useCallback(() => {
    setStep(1);
    setWorkDescription(""); setWorkPerformed(""); setPartsUsed(""); setHoursSpent("");
    setVesselRunningHours(""); setWeatherConditions(""); setSeaState("");
    setConditionRating(0); setDefectFound(false); setDefectDescription("");
    setFollowUpRequired(false); setFollowUpNotes("");
    setSignatureName(fullName || "");
    photos.forEach(p => URL.revokeObjectURL(p.preview));
    setPhotos([]);
  }, [fullName, photos]);

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handlePickFiles = (files: FileList | null) => {
    if (!files || !userId) return;
    const arr = Array.from(files).slice(0, 8 - photos.length);
    const next = arr.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
    }));
    setPhotos(prev => [...prev, ...next]);
  };

  const removePhoto = (idx: number) => {
    setPhotos(prev => {
      const copy = [...prev];
      const [removed] = copy.splice(idx, 1);
      if (removed) URL.revokeObjectURL(removed.preview);
      return copy;
    });
  };

  const uploadPhotos = async (): Promise<string[]> => {
    if (!userId || photos.length === 0) return [];
    const urls: string[] = [];
    for (const p of photos) {
      const ext = p.file.name.split(".").pop() || "jpg";
      const path = `${userId}/${job?.id || "unknown"}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("pms-completion-photos").upload(path, p.file, {
        cacheControl: "3600", upsert: false,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("pms-completion-photos").getPublicUrl(path);
      urls.push(data.publicUrl);
    }
    return urls;
  };

  const completeMutation = useMutation({
    mutationFn: async () => {
      if (!job || !userId) throw new Error("Missing job or user");
      const photoUrls = await uploadPhotos();

      const { error: insertErr } = await supabase.from("pms_job_completions").insert({
        job_id: job.id,
        vessel_id: job.vessel_id,
        vessel_name: job.vessel_name,
        equipment_id: job.equipment_id,
        job_number: job.job_number,
        job_title: job.title,
        completed_by: userId,
        completed_by_name: fullName || signatureName,
        work_description: workDescription.trim(),
        work_performed: workPerformed.trim() || null,
        parts_used: partsUsed.trim() || null,
        hours_spent: hoursSpent ? Number(hoursSpent) : null,
        vessel_running_hours: vesselRunningHours ? Number(vesselRunningHours) : null,
        weather_conditions: weatherConditions.trim() || null,
        sea_state: seaState || null,
        condition_rating: conditionRating || null,
        defect_found: defectFound,
        defect_description: defectFound ? defectDescription.trim() : null,
        follow_up_required: followUpRequired,
        follow_up_notes: followUpRequired ? followUpNotes.trim() : null,
        photo_urls: photoUrls,
        signature_name: signatureName.trim(),
      });
      if (insertErr) throw insertErr;

      const { error: jobErr } = await supabase.from("pms_jobs").update({
        status: "completed",
        last_completed_at: new Date().toISOString(),
        last_completed_by: userId,
        last_completed_by_name: fullName,
      }).eq("id", job.id);
      if (jobErr) throw jobErr;
    },
    onSuccess: () => {
      toast({ title: "Job completed", description: "Completion record saved with photos and details." });
      qc.invalidateQueries({ queryKey: ["pms-jobs"] });
      qc.invalidateQueries({ queryKey: ["pms_jobs"] });
      qc.invalidateQueries({ queryKey: ["pms_job_completions"] });
      reset();
      onOpenChange(false);
      onCompleted?.();
    },
    onError: (e: any) => toast({
      title: "Could not save completion", description: e?.message || "Please try again", variant: "destructive",
    }),
  });

  const canProceedStep1 = workDescription.trim().length >= 10;
  const canProceedStep2 = conditionRating > 0 && (!defectFound || defectDescription.trim().length > 5);
  const canSubmit = signatureName.trim().length >= 2 && !completeMutation.isPending;

  const progress = useMemo(() => Math.round((step / 3) * 100), [step]);

  if (!job) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <DialogTitle>Complete Job</DialogTitle>
          </div>
          <DialogDescription className="space-y-1">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline" className="font-mono">{job.job_number}</Badge>
              <span className="font-display text-foreground">{job.title}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><Ship className="w-3 h-3" />{job.vessel_name}</span>
              {job.department && <span className="flex items-center gap-1"><Cog className="w-3 h-3" />{job.department}</span>}
              {job.estimated_duration_hours && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Est {job.estimated_duration_hours}h</span>}
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="space-y-2">
          <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
            <span className={step >= 1 ? "text-teal font-bold" : ""}>1. Work Done</span>
            <span className={step >= 2 ? "text-teal font-bold" : ""}>2. Condition & Photos</span>
            <span className={step >= 3 ? "text-teal font-bold" : ""}>3. Sign-Off</span>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>

        {/* STEP 1: Work Done */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1 text-xs">
                <Wrench className="w-3.5 h-3.5 text-teal" />
                Work Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Briefly describe what maintenance was performed (min 10 chars)…"
                value={workDescription}
                onChange={(e) => setWorkDescription(e.target.value)}
                rows={3}
                maxLength={500}
              />
              <p className="text-[10px] text-muted-foreground text-right">{workDescription.length}/500</p>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1 text-xs">
                <ListChecks className="w-3.5 h-3.5 text-teal" />
                Detailed Steps Performed
              </Label>
              <Textarea
                placeholder="e.g. 1) Isolated unit per LOTO. 2) Drained oil. 3) Replaced filter element. 4) Refilled with 12L SAE 40. 5) Run-up and verified pressures…"
                value={workPerformed}
                onChange={(e) => setWorkPerformed(e.target.value)}
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Time Spent (hours)</Label>
                <Input type="number" step="0.25" min="0" placeholder="2.5"
                  value={hoursSpent} onChange={(e) => setHoursSpent(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1"><Gauge className="w-3.5 h-3.5" />Vessel Running Hours</Label>
                <Input type="number" min="0" placeholder="12450"
                  value={vesselRunningHours} onChange={(e) => setVesselRunningHours(e.target.value)} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Parts / Consumables Used</Label>
              <Textarea
                placeholder="Part numbers, quantities, lubricants used…"
                value={partsUsed} onChange={(e) => setPartsUsed(e.target.value)} rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Weather</Label>
                <Input placeholder="e.g. Clear, 18°C, NE 12kt"
                  value={weatherConditions} onChange={(e) => setWeatherConditions(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Sea State</Label>
                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={seaState} onChange={(e) => setSeaState(e.target.value)}>
                  <option value="">—</option>
                  {SEA_STATES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
              <Button disabled={!canProceedStep1} onClick={() => setStep(2)} className="bg-teal hover:bg-teal-light text-white">
                Next: Condition & Photos
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2: Condition & Photos */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1">
                <Star className="w-3.5 h-3.5 text-amber-500" />
                Equipment Condition After Work <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" onClick={() => setConditionRating(n)}
                    className={`flex-1 py-3 rounded-lg border-2 transition-all ${
                      conditionRating === n
                        ? "border-teal bg-teal/10 scale-105"
                        : "border-border hover:border-teal/50"
                    }`}>
                    <Star className={`w-5 h-5 mx-auto ${
                      conditionRating >= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground"
                    }`} />
                    <p className={`text-[10px] mt-1 font-display ${
                      conditionRating === n ? CONDITION_COLORS[n] : "text-muted-foreground"
                    }`}>
                      {CONDITION_LABELS[n]}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <Card className="p-3 bg-muted/30 border-dashed">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1">
                    <ImagePlus className="w-3.5 h-3.5 text-teal" />
                    Photos ({photos.length}/8)
                  </Label>
                  <div className="flex gap-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      capture="environment"
                      className="hidden"
                      onChange={(e) => { handlePickFiles(e.target.files); e.target.value = ""; }}
                    />
                    <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]"
                      onClick={() => fileInputRef.current?.click()} disabled={photos.length >= 8}>
                      <Camera className="w-3 h-3 mr-1" /> Take Photo
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]"
                      onClick={() => {
                        const input = document.createElement("input");
                        input.type = "file"; input.accept = "image/*"; input.multiple = true;
                        input.onchange = () => handlePickFiles(input.files);
                        input.click();
                      }} disabled={photos.length >= 8}>
                      <Upload className="w-3 h-3 mr-1" /> Upload
                    </Button>
                  </div>
                </div>
                {photos.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground text-center py-4">
                    📸 Capture before / during / after photos to document the work — strongly recommended for class evidence.
                  </p>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {photos.map((p, i) => (
                      <div key={i} className="relative group aspect-square rounded-md overflow-hidden border">
                        <img src={p.preview} alt={`upload ${i + 1}`} className="w-full h-full object-cover" />
                        <button type="button"
                          onClick={() => removePhoto(i)}
                          className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition">
                          <X className="w-3 h-3" />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">
                          {(p.file.size / 1024).toFixed(0)} KB
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 flex-1">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-xs font-display">Defect / Anomaly Found?</p>
                    <p className="text-[10px] text-muted-foreground">Auto-creates a defect record for the chief engineer.</p>
                  </div>
                </div>
                <Switch checked={defectFound} onCheckedChange={setDefectFound} />
              </div>
              {defectFound && (
                <Textarea className="mt-2" placeholder="Describe defect, severity, immediate actions taken…"
                  value={defectDescription} onChange={(e) => setDefectDescription(e.target.value)} rows={2} />
              )}
            </Card>

            <Card className="p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 flex-1">
                  <Sparkles className="w-4 h-4 text-teal mt-0.5" />
                  <div>
                    <p className="text-xs font-display">Follow-Up Work Required?</p>
                    <p className="text-[10px] text-muted-foreground">Flag for next port / superintendent review.</p>
                  </div>
                </div>
                <Switch checked={followUpRequired} onCheckedChange={setFollowUpRequired} />
              </div>
              {followUpRequired && (
                <Textarea className="mt-2" placeholder="What needs to be done, by when, with what resources…"
                  value={followUpNotes} onChange={(e) => setFollowUpNotes(e.target.value)} rows={2} />
              )}
            </Card>

            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button disabled={!canProceedStep2} onClick={() => setStep(3)} className="bg-teal hover:bg-teal-light text-white">
                Next: Sign-Off
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Sign-Off Summary */}
        {step === 3 && (
          <div className="space-y-4">
            <Card className="p-4 bg-gradient-to-br from-teal/5 to-emerald-500/5 border-teal/30">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardCheck className="w-4 h-4 text-teal" />
                <p className="text-sm font-display">Completion Summary</p>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
                <dt className="text-muted-foreground">Job</dt>
                <dd className="font-display truncate">{job.job_number} · {job.title}</dd>
                <dt className="text-muted-foreground">Vessel</dt>
                <dd>{job.vessel_name}</dd>
                <dt className="text-muted-foreground">Time spent</dt>
                <dd>{hoursSpent ? `${hoursSpent} h` : "—"}</dd>
                <dt className="text-muted-foreground">Condition</dt>
                <dd className={CONDITION_COLORS[conditionRating] || ""}>
                  {conditionRating > 0 ? `${CONDITION_LABELS[conditionRating]} (${conditionRating}/5)` : "—"}
                </dd>
                <dt className="text-muted-foreground">Photos</dt>
                <dd>{photos.length} attached</dd>
                <dt className="text-muted-foreground">Defect</dt>
                <dd>{defectFound ? "⚠ Yes" : "None"}</dd>
                <dt className="text-muted-foreground">Follow-up</dt>
                <dd>{followUpRequired ? "Required" : "None"}</dd>
              </dl>
            </Card>

            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1">
                <FileSignature className="w-3.5 h-3.5 text-teal" />
                Digital Signature <span className="text-red-500">*</span>
              </Label>
              <Input placeholder="Type your full name to sign"
                value={signatureName} onChange={(e) => setSignatureName(e.target.value)} />
              <p className="text-[10px] text-muted-foreground">
                By signing, you certify the above work was completed in accordance with vessel SMS and applicable class requirements.
              </p>
            </div>

            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(2)} disabled={completeMutation.isPending}>Back</Button>
              <Button disabled={!canSubmit} onClick={() => completeMutation.mutate()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {completeMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-2" /> Submit Completion</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PMSJobCompletionDialog;
